import {
    CircularProgress,
    createStyles,
    Grid,
    Grow,
    Theme,
    Typography,
    withStyles,
    WithStyles
} from "@material-ui/core";
import { GridJustification } from "@material-ui/core/Grid";
import React from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import TimerProvider from "../../../providers/TimerProvider";
import { IAppDispatch, IAppState } from "../../../store";
import * as TimerStore from "../../../store/TimerStore";

type CountdownTimerCardProps = WithStyles<typeof styles> &
    TimerStore.ITimerStoreState &
    typeof TimerStore.actionCreators & {
        date: string;
        entityId: string;

        biggerFont?: boolean;
        justifyContent?: GridJustification;
        maxWidth?: number | string;

        hideDayHourZeroes?: boolean;
        useCircleLoader?: boolean;
    };

const mobileScale = 0.85;
const digitFontSize = 20;
const digitFontSizeBigger = 24;
const captionFontSize = 10;
const captionFontSizeBigger = 12;
const maxWidth = 150;
const maxWidthMobile = 115;

const circleMinutes = 3;
const circleMinutesLowTime = 2;

const styles = (theme: Theme) =>
    createStyles({
        circleLoader: {
            [theme.breakpoints.down("xs")]: {
                height: "27px !important",
                width: "27px !important"
            },
            color: theme.palette.primary.main,
            height: "33px !important",
            transform: "rotate(90deg) scaleX(-1) !important",
            transition: "color 0.3s ease",
            width: "33px !important"
        },
        circleLoaderTimeLow: { color: theme.palette.error.main },
        circleLoaderWrapper: {
            marginRight: theme.spacing(),
            marginTop: 3
        },
        digit: {
            [theme.breakpoints.down("xs")]: {
                fontSize: digitFontSize * mobileScale
            },
            fontSize: digitFontSize,
            lineHeight: 1.15,
            color: theme.palette.common.white
        },
        digitBigger: {
            [theme.breakpoints.down("xs")]: {
                fontSize: digitFontSizeBigger * mobileScale
            },
            fontSize: digitFontSizeBigger,
            fontWeight: "bold",
            lineHeight: 1.15,
            color: theme.palette.common.white
        },
        digitBold: { fontWeight: "bold", color: theme.palette.common.white },
        digitCaption: {
            [theme.breakpoints.down("xs")]: {
                fontSize: captionFontSize * mobileScale
            },
            fontSize: captionFontSize,
            color: theme.palette.common.white
        },
        digitCaptionBigger: {
            [theme.breakpoints.down("xs")]: {
                fontSize: captionFontSizeBigger * mobileScale
            },
            fontSize: captionFontSizeBigger,
            color: theme.palette.common.white
        },
        digitColon: {
            [theme.breakpoints.down("xs")]: {
                fontSize: digitFontSize * mobileScale
            },
            fontSize: digitFontSize,
            lineHeight: 1.15,
            color: theme.palette.common.white
        },
        digitColonBigger: {
            [theme.breakpoints.down("xs")]: {
                fontSize: digitFontSizeBigger * mobileScale
            },
            fontSize: digitFontSizeBigger,
            lineHeight: 1.15,
            color: theme.palette.common.white
        },
        root: {
            [theme.breakpoints.down("xs")]: { maxWidth: maxWidthMobile },
            maxWidth,
            textAlign: "center"
        }
    });

class CountdownTimer extends React.PureComponent<CountdownTimerCardProps, {}> {
    public componentDidMount() {}

    public componentWillUnmount() {}

    public render() {
        const classes = this.props.classes;

        let rootStyles: React.CSSProperties = {};
        if (this.props.maxWidth) {
            rootStyles = { maxWidth: this.props.maxWidth };
        }

        const { days, hours, minutes, seconds } = TimerProvider.RemainingTime(
            this.props.date,
            this.props.clientTimeOffsetInMilliseconds
        );

        const showDays =
            !this.props.hideDayHourZeroes ||
            (this.props.hideDayHourZeroes && days > 0);
        const showHours =
            !this.props.hideDayHourZeroes ||
            (this.props.hideDayHourZeroes &&
                (days > 0 || (days <= 0 && hours > 0)));

        const showLoader =
            !!this.props.useCircleLoader &&
            days <= 0 &&
            hours <= 0 &&
            minutes < circleMinutes;
        const lowTime =
            days <= 0 && hours <= 0 && minutes < circleMinutesLowTime;

        return (
            <div className={classes.root} style={rootStyles}>
                <Grid
                    container={true}
                    spacing={0}
                    justifyContent={this.props.justifyContent || "flex-start"}
                >
                    {this.renderCircleLoader(
                        showLoader,
                        lowTime,
                        minutes,
                        seconds
                    )}

                    {showDays && this.renderTimeColumn(days, "days", lowTime)}
                    {showDays && this.renderColon()}

                    {showHours && this.renderTimeColumn(hours, "hrs", lowTime)}
                    {showHours && this.renderColon()}

                    {this.renderTimeColumn(minutes, "mins", lowTime)}
                    {this.renderColon()}

                    {this.renderTimeColumn(seconds, "secs", lowTime)}
                </Grid>
            </div>
        );
    }

    private renderCircleLoader = (
        showLoader: boolean,
        lowTime: boolean,
        minutes: number,
        seconds: number
    ) => {
        const classes = this.props.classes;

        const numerator = minutes * 60 + seconds;
        const denominator = (circleMinutes + 1) * 60;
        const percentDone = 100 - (numerator / denominator) * 100;

        return (
            <Grow in={showLoader} mountOnEnter={true} unmountOnExit={true}>
                <div className={classes.circleLoaderWrapper}>
                    <CircularProgress
                        className={`${classes.circleLoader} ${
                            lowTime ? classes.circleLoaderTimeLow : ""
                        }`}
                        thickness={6}
                        variant="static"
                        value={percentDone}
                    />
                </div>
            </Grow>
        );
    };

    private renderTimeColumn = (
        amt: number,
        text: string,
        lowTime: boolean
    ) => {
        const digitClass = this.props.biggerFont
            ? this.props.classes.digitBigger
            : this.props.classes.digit;
        const captionClass = this.props.biggerFont
            ? this.props.classes.digitCaptionBigger
            : this.props.classes.digitCaption;
        const lowTimeClass = lowTime ? this.props.classes.digitBold : "";

        return (
            <Grid item={true} xs={2}>
                <Typography
                    className={`${digitClass} ${lowTimeClass}`}
                    variant="subtitle1"
                    display="block"
                >
                    {amt.toString().padStart(2, "0")}
                </Typography>
                <Typography
                    className={captionClass}
                    variant="caption"
                    display="block"
                >
                    {text}
                </Typography>
            </Grid>
        );
    };

    private renderColon = () => {
        const digitClass = this.props.biggerFont
            ? this.props.classes.digitColonBigger
            : this.props.classes.digitColon;

        return (
            <Grid item={true} xs={1}>
                <Typography className={digitClass} variant="subtitle1">
                    :
                </Typography>
            </Grid>
        );
    };
}

const reduxState = (state: IAppState) => state.timerStoreState;
const reduxDispatch = (dispatch: IAppDispatch) =>
    bindActionCreators(TimerStore.actionCreators, dispatch);

export default withStyles(styles)(
    connect(reduxState, reduxDispatch)(CountdownTimer)
);
